"use client";

import React, { useState, useMemo } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import {
  ChevronRight,
  ShieldCheck,
  Lock,
  FileText,
  Search,
  Building2,
  Phone,
  Mail,
  Globe,
  CheckCircle2,
  ArrowRight,
  HelpCircle,
  Car,
  UserCheck,
  CreditCard,
  AlertTriangle,
  Scale,
  Sparkles,
  MapPin,
  Clock,
  KeyRound,
  Eye,
  FileCheck,
  ShieldAlert,
} from "lucide-react";

const SimplePageBackground = dynamic(
  () => import("@/components/SimplePageBackground").then((m) => m.SimplePageBackground),
  { ssr: true }
);

const IKFooter = dynamic(
  () => import("@/components/IKFooter").then((m) => m.IKFooter),
  { ssr: true }
);

interface PolicySection {
  id: number;
  title: string;
  category: string;
  icon: any;
  content: string[];
}

const POLICY_CLAUSES: PolicySection[] = [
  {
    id: 1,
    title: "1. SERVICES",
    category: "Services & KYC",
    icon: Car,
    content: [
      "I K TOURS AND TRAVELS may provide Self-Drive Car Rentals, Self-Ride Bike Rentals, Chauffeur-Driven Car Services, Captain/Rider-Driven Bike Services, Vehicle Pickup & Drop Services, Travel and transportation services, and other services displayed on the platform.",
      "Vehicle availability, pricing, packages, locations, kilometre limits and other conditions may vary depending on the vehicle, location, date, booking type and applicable package.",
    ],
  },
  {
    id: 2,
    title: "2. CUSTOMER ELIGIBILITY",
    category: "Services & KYC",
    icon: UserCheck,
    content: [
      "For Self-Drive/Self-Ride bookings, the Customer must meet the minimum age requirement specified by the Company; hold a valid Driving Licence applicable to the vehicle category; provide valid identification and address information; complete required KYC; provide accurate and genuine information; and satisfy all applicable rental requirements.",
      "The Company may refuse or cancel a booking if mandatory eligibility or verification requirements are not satisfied. A booking may not be transferred without prior Company approval.",
    ],
  },
  {
    id: 3,
    title: "3. CUSTOMER REGISTRATION & ACCOUNT",
    category: "Services & KYC",
    icon: KeyRound,
    content: [
      "The Customer is responsible for accurate and current information. The Customer must not create an account using false information, use another person's identity/documents, share account credentials or OTPs, or allow another person to use a vehicle booked in their name without authorization.",
      "The Company may suspend or terminate an account for fraud, misuse, abuse or violation of these Terms.",
    ],
  },
  {
    id: 4,
    title: "4. KYC & DOCUMENT VERIFICATION",
    category: "Services & KYC",
    icon: FileCheck,
    content: [
      "The Customer may be required to provide Aadhaar or other approved identity document, Driving Licence, document images, recent photograph/selfie, address details and other documents reasonably required.",
      "The Company may verify the Driving Licence through an applicable verification service/API and may refuse delivery or cancel a booking if verification fails, the licence is expired/invalid, the licence does not authorize the vehicle category, documents appear fraudulent/altered/inconsistent, or KYC is incomplete.",
    ],
  },
  {
    id: 5,
    title: "5. BOOKING CONFIRMATION",
    category: "Services & KYC",
    icon: CheckCircle2,
    content: [
      "A booking is confirmed only after required information and KYC are completed where applicable, required payment is successfully received, and the Company confirms the booking through the platform, WhatsApp, email, SMS or another authorized communication.",
      "A failed, pending, reversed or unsuccessful payment does not constitute a confirmed booking.",
    ],
  },
  {
    id: 6,
    title: "6. VEHICLE AVAILABILITY",
    category: "Services & KYC",
    icon: Car,
    content: [
      "Vehicle availability is subject to operational and real-time inventory. Where necessary, the Company may provide the selected vehicle, the same model/variant, or a reasonably similar vehicle, with communication where reasonably possible.",
    ],
  },
  {
    id: 7,
    title: "7. SELF-DRIVE / SELF-RIDE",
    category: "Services & KYC",
    icon: ShieldCheck,
    content: [
      "Self-Drive means the Customer operates the car without a Company-provided chauffeur. Self-Ride means the Customer operates the bike without a Company-provided rider/captain.",
      "The Customer must operate the vehicle responsibly and only during the authorized booking period and must not permit an unauthorized person to operate it.",
    ],
  },
  {
    id: 8,
    title: "8. CHAUFFEUR / CAPTAIN BOOKINGS",
    category: "Chauffeur & Trips",
    icon: UserCheck,
    content: [
      "For Chauffeur/Captain bookings, the vehicle is operated by a Company-appointed or authorized driver/rider.",
      "The Customer must not instruct the driver/rider to drive recklessly, violate traffic laws, exceed legally permitted limits, enter unsafe/prohibited areas, transport prohibited goods, or perform unlawful activities. The driver/rider may refuse unsafe, unlawful or prohibited instructions.",
    ],
  },
  {
    id: 9,
    title: "9. CHAUFFEUR TRIP TYPES",
    category: "Chauffeur & Trips",
    icon: MapPin,
    content: [
      "For chauffeur bookings the platform may provide: One Way: Pickup Location → Drop Location; Round Trip: Pickup Location → Sightseeing Location(s) → Drop Location.",
      "Applicable distance, duration, package limits and additional charges will be determined according to the Company's configured booking rules and applicable package displayed at booking.",
    ],
  },
  {
    id: 10,
    title: "10. SAWANTWADI BASE KM POLICY",
    category: "Chauffeur & Trips",
    icon: MapPin,
    content: [
      "For applicable chauffeur bookings operated from the Company's Sawantwadi base, if pickup is outside Sawantwadi the Customer may be shown: 'KM will start from Sawantwadi.' If drop is outside Sawantwadi the Customer may be shown: 'KM will end at Sawantwadi.'",
      "Where applicable, Google Maps or another configured mapping service may be used to calculate route distance for pricing.",
    ],
  },
  {
    id: 11,
    title: "11. RENTAL PERIOD & DAY CALCULATION",
    category: "Chauffeur & Trips",
    icon: Clock,
    content: [
      "Rental duration is calculated according to applicable booking rules displayed on the platform. For Self-Drive/Self-Ride, applicable start and end date/time rules will be displayed during booking.",
      "Additional-day, additional-hour and late-return charges will be calculated according to applicable pricing rules.",
    ],
  },
  {
    id: 12,
    title: "12. LATE RETURN / LATE PICKUP",
    category: "Chauffeur & Trips",
    icon: Clock,
    content: [
      "The Customer must collect and return the vehicle at the agreed date, time and location.",
      "Late return may result in extra-hour charges, additional-day charges, extra kilometre charges, operational/recovery charges and other applicable charges. Continued possession after expiry without authorization may be treated as unauthorized use.",
    ],
  },
  {
    id: 13,
    title: "13. VEHICLE PICKUP & DELIVERY",
    category: "Pickup & Inspections",
    icon: Car,
    content: [
      "At pickup, the Customer may be required to complete OTP verification and KYC, verify identity, inspect the vehicle, confirm vehicle condition, odometer and fuel/charge level, review photographs and accept the digital rental agreement.",
      "The vehicle may not be released until mandatory requirements are completed.",
    ],
  },
  {
    id: 14,
    title: "14. VEHICLE CONDITION",
    category: "Pickup & Inspections",
    icon: Eye,
    content: [
      "Vehicle condition may be recorded through photographs, videos, odometer readings, inspection reports, digital records and AI-assisted inspection systems where available.",
      "The Customer must inspect the vehicle at pickup and immediately report visible damage not already recorded.",
    ],
  },
  {
    id: 15,
    title: "15. RETURN INSPECTION",
    category: "Pickup & Inspections",
    icon: Eye,
    content: [
      "At return, the Company may inspect exterior, interior, tyres/wheels, glass, lights, accessories, odometer, fuel/charge level, mechanical condition, vehicle documents/accessories and new damage.",
      "Photographs, videos, inspection reports and other evidence may be used.",
    ],
  },
  {
    id: 16,
    title: "16. DAMAGE & LOSS",
    category: "Pickup & Inspections",
    icon: AlertTriangle,
    content: [
      "The Customer shall be responsible, to the extent permitted by applicable law, for damage, loss or theft attributable to the Customer, an unauthorized driver, negligence, misuse or breach of these Terms.",
      "This may include accident damage, body/interior/glass/tyre/wheel damage, accessory/key loss, wrong fuel, misuse-related mechanical damage, prohibited water/flood damage, reckless-driving damage, towing/recovery expenses and other reasonable documented losses.",
    ],
  },
  {
    id: 17,
    title: "17. ACCIDENT, COLLISION & ACCIDENT-RELATED LIABILITY",
    category: "Pickup & Inspections",
    icon: ShieldAlert,
    content: [
      "In any accident, collision, overturning, theft, fire or other incident, the Customer must immediately inform I K TOURS AND TRAVELS and follow Company instructions.",
      "The Customer shall be responsible, to the extent permitted by law, for accident-related losses, damages, expenses, liabilities and recovery costs to the extent not covered/reimbursed by the applicable insurance policy or excluded from coverage.",
      "Insurance coverage does not automatically release contractual responsibility for amounts not covered or reimbursed.",
    ],
  },
  {
    id: 18,
    title: "18. RENTAL / LOSS-OF-USE CHARGES AFTER ACCIDENT",
    category: "Pickup & Inspections",
    icon: Clock,
    content: [
      "If a vehicle is involved in an accident or suffers damage during the booking and becomes unavailable for rental, it may remain unavailable until fully repaired, inspected, restored to acceptable rental condition and officially released back into the fleet.",
      "Applicable rental/day or loss-of-use charges may be charged for the period during which the vehicle remains unavailable due to the accident.",
    ],
  },
  {
    id: 19,
    title: "19. AI DAMAGE DETECTION",
    category: "Pickup & Inspections",
    icon: Eye,
    content: [
      "Where available, AI-assisted technology may compare vehicle photographs before and after a trip and identify potential new damage.",
      "AI reports are an operational assessment tool and may be reviewed by the Company. Photographs, inspection reports, AI reports, videos, timestamps and other evidence may be considered.",
    ],
  },
  {
    id: 20,
    title: "20. SECURITY DEPOSIT",
    category: "Deposit & Rates",
    icon: CreditCard,
    content: [
      "For applicable Self-Drive/Self-Ride bookings, a security deposit will be collected. Current configured deposits: Cars – ₹5,000; Bikes – ₹3,000.",
      "These amounts may change in future and the applicable amount will be displayed during booking. Security Deposit is not applicable to Chauffeur/Captain bookings unless specifically stated.",
    ],
  },
  {
    id: 21,
    title: "21. SECURITY DEPOSIT REFUND",
    category: "Deposit & Rates",
    icon: CreditCard,
    content: [
      "After return, deductions may be calculated for damage, extra kilometres, extra time, fuel/charging differences, missing accessories, excessive cleaning where applicable, towing/recovery and other applicable contractual charges.",
      "The remaining refundable amount, if any, will be processed through the applicable refund method. Bank/payment gateway processing times may vary.",
    ],
  },
  {
    id: 22,
    title: "22. FUEL / CHARGING",
    category: "Deposit & Rates",
    icon: Car,
    content: [
      "Applicable fuel/charging conditions will be displayed during booking. The Customer may be charged for fuel/charging differences.",
      "Damage caused by wrong fuel or improper charging may be recoverable.",
    ],
  },
  {
    id: 23,
    title: "23. KILOMETRE LIMIT & EXTRA KM",
    category: "Deposit & Rates",
    icon: MapPin,
    content: [
      "Where a booking includes a kilometre limit, included kilometres will be displayed during booking.",
      "Excess kilometres will be charged at the applicable rate (e.g. 150 KM included + ₹15/km extra). Actual limits/rates may vary by vehicle/package.",
    ],
  },
  {
    id: 24,
    title: "24. CHAUFFEUR PACKAGE LIMITS",
    category: "Deposit & Rates",
    icon: MapPin,
    content: [
      "Chauffeur packages may include base fare, included kilometres, included hours, extra kilometre rate and extra hour rate (e.g. ₹3,500 – 150 KM / 12 Hours; Extra KM – ₹15/km; Extra Hour – ₹99/hour).",
      "Actual package rates are vehicle-wise and configurable. Exceeding the applicable kilometre or time limit attracts additional charges.",
    ],
  },
  {
    id: 25,
    title: "25. CONVENIENCE FEE",
    category: "Deposit & Rates",
    icon: CreditCard,
    content: [
      "Current configured convenience fees: Car – ₹99; Bike – ₹49.",
      "The Company may change these charges in future. The applicable fee will be displayed during checkout.",
    ],
  },
  {
    id: 26,
    title: "26. PREMIUM SUPPORT",
    category: "Deposit & Rates",
    icon: ShieldCheck,
    content: [
      "Premium Support is an optional add-on. Current configured charges: Car – ₹99; Bike – ₹49.",
      "It is charged only if selected. Pricing or scope may be modified in future.",
    ],
  },
  {
    id: 27,
    title: "27. PAYMENTS",
    category: "Deposit & Rates",
    icon: CreditCard,
    content: [
      "The Customer agrees to pay all applicable checkout charges including base fare, convenience fee, Premium Support if selected, GST/taxes, security deposit, extra kilometre charges, extra time charges, damage charges and other applicable charges.",
      "Payments may be processed through third-party gateways.",
    ],
  },
  {
    id: 28,
    title: "28. TAXES",
    category: "Deposit & Rates",
    icon: CreditCard,
    content: [
      "Applicable GST and statutory taxes will be charged according to applicable law and reflected in the booking/invoice where required.",
    ],
  },
  {
    id: 29,
    title: "29. CANCELLATION",
    category: "Cancellations & Refunds",
    icon: AlertTriangle,
    content: [
      "Cancellation is subject to the cancellation policy applicable to the particular booking.",
      "The Customer must review cancellation terms before payment and submit cancellation through the prescribed platform/support channel.",
    ],
  },
  {
    id: 30,
    title: "30. REFUNDS",
    category: "Cancellations & Refunds",
    icon: CreditCard,
    content: [
      "Where a refund is applicable, the amount will be determined according to the applicable cancellation/refund policy and may be subject to cancellation charges, non-refundable charges, deductions and other disclosed booking conditions.",
      "Processing time may depend on the payment gateway and banking institution.",
    ],
  },
  {
    id: 31,
    title: "31. COUPONS & OFFERS",
    category: "Cancellations & Refunds",
    icon: Sparkles,
    content: [
      "Coupons and promotional offers are subject to individual terms, including eligible vehicles/customers, minimum booking value, validity, usage limits, maximum discount, applicable service and other restrictions.",
      "Coupons cannot be exchanged for cash unless stated. Benefits obtained through misuse, fraud or unauthorized activity may be cancelled.",
    ],
  },
  {
    id: 32,
    title: "32. WALLET & CASHBACK",
    category: "Cancellations & Refunds",
    icon: CreditCard,
    content: [
      "Where available, cashback is subject to promotional rules; wallet balance may be used for eligible bookings.",
      "Cashback may have validity/usage restrictions; wallet balances may not necessarily be withdrawable as cash; fraudulent or abusive activity may result in cancellation or suspension.",
    ],
  },
  {
    id: 33,
    title: "33. REFERRAL PROGRAM",
    category: "Cancellations & Refunds",
    icon: Sparkles,
    content: [
      "Referral benefits are subject to displayed referral terms.",
      "The Company may restrict benefits for self-referrals, duplicate accounts, fraud, manipulation, abuse or unauthorized activity.",
    ],
  },
  {
    id: 34,
    title: "34. PROHIBITED USE",
    category: "Safety & Tracking",
    icon: ShieldAlert,
    content: [
      "Vehicles must NOT be used for racing, speed testing, stunts, competitive driving, unauthorized off-road driving, illegal activities, transporting prohibited substances/hazardous materials without authorization, towing without permission, unauthorized commercial passenger transportation, driving under the influence, any activity prohibited by law, or any use prohibited by the Company or manufacturer.",
      "Serious misuse may result in immediate termination and vehicle recovery.",
    ],
  },
  {
    id: 35,
    title: "35. DRIVING UNDER THE INFLUENCE",
    category: "Safety & Tracking",
    icon: ShieldAlert,
    content: [
      "Driving under the influence of alcohol, narcotics or any intoxicating substance is strictly prohibited.",
      "Resulting accident, damage, loss, penalty or legal consequence may be the responsibility of the person responsible, subject to applicable law.",
    ],
  },
  {
    id: 36,
    title: "36. TRAFFIC VIOLATIONS & FINES",
    category: "Safety & Tracking",
    icon: Scale,
    content: [
      "The Customer is responsible for traffic violations, parking charges, tolls, penalties and other charges attributable to use during the booking period, unless otherwise specified.",
      "The Company may recover legitimate documented charges.",
    ],
  },
  {
    id: 37,
    title: "37. ACCIDENT PROCEDURE",
    category: "Safety & Tracking",
    icon: ShieldAlert,
    content: [
      "In an accident, the Customer must ensure safety, contact emergency services where necessary, immediately inform I K TOURS AND TRAVELS, follow Company instructions, cooperate with authorities/insurance representatives, provide accurate information and evidence, and not abandon the vehicle without authorization except for safety.",
      "Failure to promptly report may affect insurance processing and increase responsibility where permitted by law.",
    ],
  },
  {
    id: 38,
    title: "38. BREAKDOWN / MECHANICAL FAILURE",
    category: "Safety & Tracking",
    icon: AlertTriangle,
    content: [
      "In genuine mechanical failure, immediately contact the Company. Do not authorize major repairs without Company approval unless immediate action is reasonably necessary for safety.",
      "Damage from misuse, negligence, wrong fuel, unauthorized modification or prohibited use may be recoverable.",
    ],
  },
  {
    id: 39,
    title: "39. INSURANCE",
    category: "Safety & Tracking",
    icon: ShieldCheck,
    content: [
      "Where applicable, vehicles may be covered under insurance policies subject to coverage, conditions, exclusions, deductibles, limits and claim requirements.",
      "Insurance does not automatically cover every accident, damage or loss. The Customer remains responsible for amounts not covered or reimbursed, subject to applicable law and the rental agreement.",
    ],
  },
  {
    id: 40,
    title: "40. GPS / LIVE VEHICLE TRACKING",
    category: "Safety & Tracking",
    icon: MapPin,
    content: [
      "Vehicles may be equipped with GPS/telematics for location, fleet management, safety, theft prevention, recovery, operational support and incident investigation.",
      "The Customer must not tamper with, disable, remove or interfere with tracking equipment.",
    ],
  },
  {
    id: 41,
    title: "41. DASHCAM / VEHICLE MONITORING",
    category: "Safety & Tracking",
    icon: Eye,
    content: [
      "Where installed, dashcams or monitoring systems may record for safety, security, accident investigation, insurance claims, fraud prevention, recovery and legal/compliance purposes.",
      "Recordings may be retained and disclosed where reasonably required or as required by law.",
    ],
  },
  {
    id: 42,
    title: "42. CUSTOMER RESPONSIBILITY FOR VEHICLE",
    category: "Safety & Tracking",
    icon: ShieldCheck,
    content: [
      "The Customer must keep the vehicle reasonably clean, protect it against theft, park responsibly, use correct fuel/charging, follow traffic laws and manufacturer instructions, keep keys/accessories secure and return the vehicle in the agreed condition.",
    ],
  },
  {
    id: 43,
    title: "43. SMOKING & EXCESSIVE CLEANING",
    category: "Safety & Tracking",
    icon: AlertTriangle,
    content: [
      "Smoking or activities causing excessive smell, stains or contamination may attract applicable cleaning charges where specified.",
      "The Customer may be charged for exceptional cleaning or restoration caused by misuse.",
    ],
  },
  {
    id: 44,
    title: "44. UNAUTHORIZED DRIVER",
    category: "Safety & Tracking",
    icon: ShieldAlert,
    content: [
      "Only the Customer or an authorized driver may operate a Self-Drive/Self-Ride vehicle.",
      "Allowing an unauthorized person to drive may result in cancellation/termination, vehicle recovery, security deposit deductions, additional charges, responsibility for resulting damage/loss and other lawful action.",
    ],
  },
  {
    id: 45,
    title: "45. VEHICLE RECOVERY",
    category: "Safety & Tracking",
    icon: ShieldAlert,
    content: [
      "Where a vehicle is not returned after expiry, abandoned, misused, used unlawfully, reported stolen, operated by an unauthorized person or subject to serious breach, the Company may take lawful steps to locate/recover it and recover reasonable associated costs, subject to law.",
    ],
  },
  {
    id: 46,
    title: "46. CHANGES TO BOOKING / VEHICLE",
    category: "Legal & Privacy",
    icon: Scale,
    content: [
      "The Company may make reasonable changes due to vehicle breakdown, maintenance, safety concerns, operational requirements, vehicle availability, government restrictions, force majeure or circumstances beyond reasonable control.",
      "Where practical, an alternative arrangement or applicable remedy will be communicated.",
    ],
  },
  {
    id: 47,
    title: "47. DRIVER CONDUCT & CUSTOMER FEEDBACK",
    category: "Legal & Privacy",
    icon: UserCheck,
    content: [
      "For Chauffeur/Captain services, customers may rate and provide feedback. The Company may investigate complaints regarding driver/rider conduct.",
      "Customers must behave respectfully and must not threaten, abuse, harass or use violence against drivers/riders.",
    ],
  },
  {
    id: 48,
    title: "48. COMMUNICATION & NOTIFICATIONS",
    category: "Legal & Privacy",
    icon: Mail,
    content: [
      "The Customer agrees to receive necessary service-related communications through WhatsApp, SMS, email, push notifications, phone calls and platform notifications, including OTPs, booking/payment confirmations, vehicle/driver assignment, reminders, pickup/drop notifications, cancellation, invoices, refunds, security deposit information and other service communications.",
    ],
  },
  {
    id: 49,
    title: "49. DIGITAL RENTAL AGREEMENT",
    category: "Legal & Privacy",
    icon: FileText,
    content: [
      "For applicable bookings, a digital Rental Agreement may be generated. The Customer may be required to review and accept it before or during handover.",
      "Booking details, accepted Terms, timestamps, OTP records, photographs and other digital records may be retained as evidence, subject to applicable law.",
    ],
  },
  {
    id: 50,
    title: "50. DIGITAL ACCEPTANCE OF TERMS",
    category: "Legal & Privacy",
    icon: FileCheck,
    content: [
      "Before completing a booking, the Customer must actively accept the applicable terms: '☐ I have read and agree to the Terms & Conditions, Cancellation & Refund Policy, Privacy Policy and applicable Rental Agreement.'",
      "The Book Now / Confirm Booking button may remain disabled until mandatory acceptance is completed. The Company may record Customer ID, Booking ID, date/time, Terms version and digital acceptance record.",
    ],
  },
  {
    id: 51,
    title: "51. PRIVACY & PERSONAL DATA",
    category: "Legal & Privacy",
    icon: Lock,
    content: [
      "The Company may collect/process information required for account creation, KYC, booking, payment, vehicle rental, driver/vehicle assignment, customer support, fraud prevention, legal compliance, insurance and operations.",
      "Personal information will be handled according to the Company's Privacy Policy and applicable law.",
    ],
  },
  {
    id: 52,
    title: "52. THIRD-PARTY SERVICES",
    category: "Legal & Privacy",
    icon: Globe,
    content: [
      "The platform may use payment gateways, Google Maps/location services, OTP services, WhatsApp communication services, email services, Driving Licence verification, AI inspection, cloud infrastructure and other technology providers.",
      "Third-party services may have their own terms and policies.",
    ],
  },
  {
    id: 53,
    title: "53. PLATFORM AVAILABILITY",
    category: "Legal & Privacy",
    icon: Globe,
    content: [
      "The Company aims for reliable platform access but does not guarantee uninterrupted or error-free operation.",
      "Interruptions may occur due to maintenance, technical/network issues, third-party failures, cybersecurity incidents, force majeure or other circumstances beyond reasonable control.",
    ],
  },
  {
    id: 54,
    title: "54. FRAUD & MISUSE",
    category: "Legal & Privacy",
    icon: ShieldAlert,
    content: [
      "The Company may investigate suspected identity fraud, fake documents, payment fraud, coupon/wallet abuse, multiple-account abuse, unauthorized vehicle use, chargeback abuse, false claims and other fraudulent activity.",
      "The Company may suspend accounts, cancel bookings and take lawful action.",
    ],
  },
  {
    id: 55,
    title: "55. LIMITATION OF LIABILITY",
    category: "Legal & Privacy",
    icon: Scale,
    content: [
      "To the maximum extent permitted by applicable law, the Company shall not be responsible for indirect, incidental, special or consequential losses arising from circumstances beyond its reasonable control.",
      "Nothing excludes or limits liability that cannot legally be excluded or limited.",
    ],
  },
  {
    id: 56,
    title: "56. FORCE MAJEURE",
    category: "Legal & Privacy",
    icon: ShieldAlert,
    content: [
      "The Company shall not be responsible for failure/delay caused by circumstances beyond reasonable control, including natural disasters, floods, cyclones, earthquakes, government restrictions, road closures, strikes, civil disturbances, war, epidemics/pandemics, major accidents, infrastructure failures, third-party outages or other force majeure events.",
    ],
  },
  {
    id: 57,
    title: "57. CUSTOMER DISPUTES",
    category: "Legal & Privacy",
    icon: Scale,
    content: [
      "Customers should first contact I K TOURS AND TRAVELS through the designated support channel.",
      "Provide Booking ID, registered mobile number, issue description, photographs/documents and supporting evidence where available. The Company will review the matter based on available records, policies and the rental agreement.",
    ],
  },
  {
    id: 58,
    title: "58. GOVERNING LAW & JURISDICTION",
    category: "Legal & Privacy",
    icon: Scale,
    content: [
      "These Terms shall be governed by laws applicable in India. Subject to applicable law and mandatory consumer protection provisions, disputes shall be subject to the jurisdiction of competent courts having jurisdiction over the Company's applicable principal place of business.",
      "Office Address: G-725/D, Main Road, Sawantwadi, Opp. Renuka Palace, Sawantwadi, Sindhudurg, Maharashtra – 416510.",
    ],
  },
  {
    id: 59,
    title: "59. CHANGES TO THESE TERMS",
    category: "Legal & Privacy",
    icon: FileText,
    content: [
      "The Company may update these Terms due to changes in law, new services, operational changes, pricing changes, security requirements or platform changes.",
      "The latest version will be published with its effective date. For confirmed bookings, the Terms applicable at booking will generally govern, subject to law.",
    ],
  },
  {
    id: 60,
    title: "60. SEVERABILITY",
    category: "Legal & Privacy",
    icon: Scale,
    content: [
      "If any provision is found invalid, unlawful or unenforceable, the remaining provisions shall continue to apply to the extent permitted by law.",
    ],
  },
  {
    id: 61,
    title: "61. ENTIRE AGREEMENT",
    category: "Legal & Privacy",
    icon: FileText,
    content: [
      "These Terms, together with the applicable Booking Confirmation, Digital Rental Agreement, Pricing Details, Cancellation & Refund Policy, Privacy Policy, Vehicle-Specific Conditions and Coupon/Promotional Terms form the applicable contractual framework for the relevant service, subject to applicable law.",
    ],
  },
  {
    id: 62,
    title: "62. CUSTOMER ACKNOWLEDGEMENT",
    category: "Legal & Privacy",
    icon: CheckCircle2,
    content: [
      "By selecting 'I Agree', 'Book Now', 'Confirm Booking', completing payment, accepting a vehicle or otherwise proceeding with the service, the Customer confirms that they have read and understood these Terms, agree to comply, have provided genuine/accurate information, understand applicable charges and cancellation/refund policy, understand vehicle responsibilities and accident/damage/loss-of-use provisions, agree to the Privacy Policy and applicable Rental Agreement.",
    ],
  },
];

const CATEGORIES = [
  "All Policy Terms",
  "Services & KYC",
  "Chauffeur & Trips",
  "Pickup & Inspections",
  "Deposit & Rates",
  "Cancellations & Refunds",
  "Safety & Tracking",
  "Legal & Privacy",
];

const HIGHLIGHT_CARDS = [
  {
    icon: Lock,
    title: "256-Bit Data Encryption",
    desc: "All identity verification documents and booking data are safeguarded using bank-grade 256-bit SSL encryption.",
    badge: "Data Protection",
  },
  {
    icon: FileCheck,
    title: "KYC & Licence Verification",
    desc: "Government IDs and Driving Licences are verified strictly for rental eligibility and legal compliance.",
    badge: "Verified Access",
  },
  {
    icon: MapPin,
    title: "GPS Telematics & Safety",
    desc: "Real-time location monitoring ensures fleet safety, 24/7 roadside assistance, and theft prevention.",
    badge: "24/7 Monitoring",
  },
  {
    icon: Scale,
    title: "Governing Law & Disclosures",
    desc: "Personal data and operational policies adhere strictly to Indian transport and data governance laws.",
    badge: "Compliant",
  },
];

export default function PrivacyPolicyPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All Policy Terms");

  const filteredClauses = useMemo(() => {
    return POLICY_CLAUSES.filter((clause) => {
      const matchesCategory =
        activeCategory === "All Policy Terms" || clause.category === activeCategory;
      const q = searchQuery.toLowerCase().trim();
      if (!q) return matchesCategory;

      const matchesText =
        clause.title.toLowerCase().includes(q) ||
        clause.content.some((text) => text.toLowerCase().includes(q));

      return matchesCategory && matchesText;
    });
  }, [searchQuery, activeCategory]);

  return (
    <SimplePageBackground>
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 flex-1 flex flex-col justify-between">
        <div>
          {/* Breadcrumb Navigation */}
          <nav
            className="flex items-center flex-wrap gap-1.5 md:gap-2 text-xs md:text-sm text-neutral-500 dark:text-neutral-400 mb-8 font-medium"
            aria-label="Breadcrumb"
          >
            <Link
              href="/"
              className="hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors"
            >
              Home
            </Link>
            <ChevronRight
              className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-600 shrink-0"
              aria-hidden
            />
            <span className="text-[#131222] dark:text-white font-semibold">
              Privacy Policy & Terms
            </span>
          </nav>

          {/* Hero Header */}
          <div className="mb-10 text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFB51D]/10 dark:bg-[#FFB51D]/20 border border-[#FFB51D]/30 mb-4">
              <ShieldCheck className="w-4 h-4 text-[#FFB51D]" aria-hidden />
              <span className="text-xs sm:text-sm font-semibold text-[#131222] dark:text-[#FFB51D]">
                I K TOURS AND TRAVELS • Legacy In Every Mile
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#131222] dark:text-white tracking-tight mb-3">
              Privacy Policy & <span className="text-[#FFB51D]">Rental Terms</span>
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              Official Terms & Conditions and Privacy Framework governing Vehicle Rentals, Chauffeur Services, KYC Verification & Personal Data Protection.
            </p>

            {/* Version & Date Metadata Badge */}
            <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-5 text-xs font-medium text-neutral-600 dark:text-neutral-300 mt-4 p-2.5 px-4 rounded-xl bg-white/70 dark:bg-neutral-900/70 border border-neutral-200/80 dark:border-neutral-800 max-w-xl mx-auto shadow-xs">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#FFB51D]" />
                Version: <span className="font-mono text-[#131222] dark:text-white font-bold">1.0</span>
              </span>
              <span className="text-neutral-300 dark:text-neutral-700">•</span>
              <span>
                Effective Date: <span className="font-mono text-[#131222] dark:text-white font-bold">20/08/2026</span>
              </span>
              <span className="text-neutral-300 dark:text-neutral-700">•</span>
              <span>
                Last Updated: <span className="font-mono text-[#131222] dark:text-white font-bold">20/08/2026</span>
              </span>
            </div>

            <div className="h-1 w-20 bg-[#FFB51D] mx-auto rounded-full mt-5" />
          </div>

          {/* Official Company Address & Info Card */}
          <div className="max-w-4xl mx-auto mb-10 p-5 md:p-6 rounded-2xl bg-white dark:bg-neutral-900/50 border border-neutral-200/80 dark:border-neutral-800 shadow-md">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
              <div className="flex items-start gap-2.5">
                <Building2 className="w-4 h-4 text-[#FFB51D] shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-[#131222] dark:text-white">Registered Office</p>
                  <p className="text-neutral-500 dark:text-neutral-400 leading-snug">
                    G-725/D, Main Road, Opp. Renuka Palace, Sawantwadi, Sindhudurg, MH - 416510
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Phone className="w-4 h-4 text-[#FFB51D] shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-[#131222] dark:text-white">Customer Support</p>
                  <p className="text-neutral-500 dark:text-neutral-400">9809538899 / 9809548899</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Mail className="w-4 h-4 text-[#FFB51D] shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-[#131222] dark:text-white">Official Email</p>
                  <p className="text-neutral-500 dark:text-neutral-400">ikbs8899@gmail.com</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Globe className="w-4 h-4 text-[#FFB51D] shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-[#131222] dark:text-white">Official Platform</p>
                  <p className="text-neutral-500 dark:text-neutral-400">www.iktravels.com</p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Highlights Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10 max-w-5xl mx-auto">
            {HIGHLIGHT_CARDS.map((card, idx) => {
              const Icon = card.icon;
              return (
                <div
                  key={idx}
                  className="group rounded-2xl border border-neutral-200/80 dark:border-neutral-800 p-5 bg-white dark:bg-neutral-900/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 bg-[#FFB51D]/10 dark:bg-[#FFB51D]/10 rounded-xl text-[#FFB51D] group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300">
                        {card.badge}
                      </span>
                    </div>
                    <h3 className="font-bold text-[#131222] dark:text-white mb-1.5 text-sm md:text-base">
                      {card.title}
                    </h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-xs leading-relaxed">
                      {card.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Search & Category Filter Navigation Bar */}
          <div className="max-w-4xl mx-auto mb-8 space-y-4">
            {/* Search Input Box */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3.5 top-3.5 text-neutral-400" />
              <input
                type="text"
                placeholder="Search policy clauses (e.g., KYC, Security Deposit, FASTag, Insurance, Driving License)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-11 pl-10 pr-4 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-xs sm:text-sm text-[#131222] dark:text-white outline-none focus:border-[#FFB51D] shadow-sm transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-3 text-xs font-semibold text-neutral-400 hover:text-neutral-600 dark:hover:text-white"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Category Filter Pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
              {CATEGORIES.map((cat) => {
                const isActive = activeCategory === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setActiveCategory(cat)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                      isActive
                        ? "bg-[#FFB51D] text-[#131222] shadow-sm"
                        : "bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-neutral-600 dark:text-neutral-400 hover:border-[#FFB51D]"
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Policy Clauses List (All 62 Sections) */}
          <div className="max-w-4xl mx-auto mb-16 space-y-4">
            {filteredClauses.length === 0 ? (
              <div className="p-10 text-center rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
                <HelpCircle className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                <p className="font-bold text-[#131222] dark:text-white text-sm">No clauses found matching your search</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Try typing another keyword or selecting a different category tab.</p>
              </div>
            ) : (
              filteredClauses.map((clause) => {
                const Icon = clause.icon;
                return (
                  <div
                    key={clause.id}
                    className="group rounded-2xl bg-white dark:bg-neutral-900/40 border border-neutral-200/80 dark:border-neutral-800 p-5 md:p-6 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-[#FFB51D]/10 dark:bg-[#FFB51D]/10 text-[#FFB51D] group-hover:scale-110 transition-transform duration-300">
                          <Icon className="w-4 h-4" />
                        </div>
                        <h3 className="text-base sm:text-lg font-extrabold text-[#131222] dark:text-white">
                          {clause.title}
                        </h3>
                      </div>
                      <span className="text-[10px] font-mono font-bold px-2.5 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
                        {clause.category}
                      </span>
                    </div>

                    <div className="space-y-2.5 pl-2 sm:pl-9">
                      {clause.content.map((p, pIdx) => (
                        <p
                          key={pIdx}
                          className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed font-normal"
                        >
                          {p}
                        </p>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Support / Assistance CTA Block */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#131222] via-neutral-900 to-[#1f1d38] p-8 md:p-10 text-white shadow-2xl border border-neutral-800">
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left max-w-xl">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFB51D]/20 text-[#FFB51D] text-xs font-semibold mb-3 border border-[#FFB51D]/30">
                    <HelpCircle className="w-3.5 h-3.5" /> Data Protection Desk
                  </div>
                  <h3 className="text-2xl font-extrabold text-white mb-2">
                    Questions about your Privacy & Terms?
                  </h3>
                  <p className="text-neutral-300 text-sm leading-relaxed">
                    Our compliance & customer support team is available 24/7 to assist with privacy requests, booking terms, or rental clarifications.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
                  <Link
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#FFB51D] text-[#131222] font-bold text-sm hover:bg-[#FFC44D] transition-all shadow-md text-center"
                  >
                    <span>Contact Support</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    href="/terms-and-conditions"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-white/20 bg-white/10 text-white font-medium text-sm hover:bg-white/20 transition-all text-center"
                  >
                    <span>Terms & Conditions</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reusable Footer */}
      <IKFooter />
    </SimplePageBackground>
  );
}
